import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import "./FaqContent.css";
import { ShimmerTable } from "react-shimmer-effects";
import parse from "react-html-parser";
import { formatCmsTextToHtml } from "@/utils/textParser";
import { Helmet } from "react-helmet";
import { trackButtonClick } from "@/utils/analytics";
import { getSupportData } from "@/api/faq";
import { globalCmsData } from "@/redux/cms/cmsSelectors";
import { useSelector } from "react-redux";

const FaqContent = () => {
  const contentRef = useRef(null);

  const [faqData, setFaqData] = useState(null);
  const [featuredTopics, setFeaturedTopics] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const globalCms = useSelector(globalCmsData);

  const onChatClick = () => {
    var deviceWidth = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    var liveWidgetId = deviceWidth > 767 ? 1003020 : 1003601;
    if (typeof LVWebWidget !== "undefined") {
      LVWebWidget.render({
        url: globalCms?.LivevoxUrl,
        uuid: globalCms?.LivevoxUUID,
        widgetId: liveWidgetId,
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await getSupportData("faqSection");

        if (data?.results?.[0]?.sections) {
          setFaqData(data);
          const sections = data.results[0].sections;

          const filteredSections = Object.fromEntries(
            Object.entries(sections).filter(([key]) => !key.startsWith("@"))
          );

          setFeaturedTopics(filteredSections);

          const firstTab = Object.keys(filteredSections)[0];
          setActiveTab(firstTab);

          const categories = Object.keys(
            filteredSections[firstTab]?.quesCategory || {}
          );
          const validCategories = categories
            .filter((cat) => !cat.startsWith("@"))
            .sort(
              (a, b) => (a.match(/\d+/)?.[0] || 0) - (b.match(/\d+/)?.[0] || 0)
            );

          setActiveCategory(validCategories[0]);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      const target = e.target;
      const clickType = target.dataset.click;

      if (clickType === "cms-live-chat") {
        onChatClick?.();
      }
    };

    const container = contentRef.current;
    container?.addEventListener("click", handleClick);
  }, [onChatClick]);

  // Memoized filtered data
  const filteredTabs = useMemo(() => {
    return Object.entries(featuredTopics)
      .filter(([key]) => !key.startsWith("@"))
      .sort(([a], [b]) => a.match(/\d+/) - b.match(/\d+/));
  }, [featuredTopics]);

  const filteredCategories = useMemo(() => {
    const currentTabData = featuredTopics[activeTab] || {};
    return Object.entries(currentTabData.quesCategory || {})
      .filter(([key]) => !key.startsWith("@"))
      .sort(([a], [b]) => a.match(/\d+/) - b.match(/\d+/));
  }, [featuredTopics, activeTab]);

  const filteredQuestions = useMemo(() => {
    const currentTabData = featuredTopics[activeTab] || {};
    const currentCategoryData = currentTabData.quesCategory?.[
      activeCategory
    ] || {
      questions: [],
    };
    return Object.entries(currentCategoryData.questions || {})
      .filter(([key]) => !key.startsWith("@"))
      .sort(([a], [b]) => a.match(/\d+/) - b.match(/\d+/));
  }, [featuredTopics, activeTab, activeCategory]);

  // Handle toggling questions
  const handleToggleQuestion = useCallback((index) => {
    setOpenQuestion((prev) => (prev === index ? null : index));
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="faq-loading-container">
        <div className="loading-content">
          {/* <div className="spinner-border text-danger mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div> */}
          <h2 className="help-topic__main-title">Loading FAQs...</h2>
          <div
            // className="faq__loader-animation"
            style={{ width: "65vw" }}
          >
            {/* <Loader /> */}
            <ShimmerTable row={5} col={5} />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="faq-error-container">
        <div className="error-content">
          <h4 className="error-text">{error}</h4>
        </div>
      </div>
    );
  }

  // Empty state
  if (filteredTabs.length === 0 || filteredCategories.length === 0) {
    return (
      <div className="faq-empty-container">
        <div className="faq-empty-content">
          <h4 className="faq-empty-text">No FAQs available at the moment.</h4>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-wrapper">
      <Helmet>
        <script async defer src={globalCms?.LivevoxScriptUrl}></script>
      </Helmet>
      <div className="faq-page container">
        {/* Header section */}
        <div className="text-center  position-relative">
          <h2 className="display-5 fw-bold text-dark mb-3 faq-content-title">
            {faqData?.title}
          </h2>
          <p className="text-muted lead faq-content-subtitle">
            {faqData?.subTitle}
          </p>
        </div>
        <p className=" text-center faq-acco_heeading">
          Frequently Asked Questions
        </p>

        {/* Tabs */}
        <div className="navv faq-nav-pills">
          {filteredTabs.map(([key, value]) => (
            <button
              key={key}
              className={`nav-link faq-nav-link faq-content-font-regular ${
                activeTab === key ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab(key);

                trackButtonClick({
                  eventName: "faq_section_click",
                  eventParams: { section_title: value.title },
                  eventCategory: "Support-Page",
                  eventLabel: "Link Clicked",
                  eventSource: "Support page tab click",
                });

                const categories = Object.keys(
                  featuredTopics[key]?.quesCategory || {}
                );

                const validCategories = categories
                  .filter((cat) => !cat.startsWith("@"))
                  .sort(([a], [b]) => a.match(/\d+/) - b.match(/\d+/));
                setActiveCategory(validCategories[0]);
              }}
            >
              {value.title}
            </button>
          ))}
        </div>

        {/* Categories and Questions */}
        <div className="row">
          <div className="col-lg-3 mb-4">
            <div className="p-3" style={{ background: "#f8f8f8" }}></div>

            <div className="list-group faq-list-group">
              {filteredCategories.map(([key, value]) => (
                <>
                  <button
                    key={key}
                    className={`faq-list-group-item faq-list-group-item-action faq-content-font-regular ${
                      activeCategory === key ? "active" : ""
                    }`}
                    onClick={() => setActiveCategory(key)}
                  >
                    {value.title}
                  </button>
                </>
              ))}
            </div>
          </div>

          <div className="col-lg-9">
            <div className="accordion" id="faqAccordion">
              <div className="p-3"></div>

              {filteredQuestions.map(([key, item], index) => {
                // Created a unique identifier using category and index
                const uniqueId = `${activeCategory}-${index}`;
                return (
                  <div
                    key={uniqueId}
                    className="accordion-item faq-accordion-item"
                  >
                    <h2 className="accordion-header" id={`heading-${uniqueId}`}>
                      <button
                        className={`accordion-button faq-accordion-button faq-content-font-regular ${
                          openQuestion !== uniqueId ? "collapsed" : ""
                        }`}
                        onClick={() => handleToggleQuestion(uniqueId)}
                        aria-expanded={openQuestion === uniqueId}
                        aria-controls={`collapse-${uniqueId}`}
                      >
                        {item.ques}
                      </button>
                    </h2>

                    <div
                      id={`collapse-${uniqueId}`}
                      className={`accordion-collapse collapse ${
                        openQuestion === uniqueId ? "show" : ""
                      } faq-accordion-body`}
                      aria-labelledby={`heading-${uniqueId}`}
                      ref={contentRef}
                      // className="accordion-body faq-accordion-body faq-content-font-regular"
                    >
                      {parse(formatCmsTextToHtml(item.ans))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqContent;
